import {
  Box,
  Container,
  Flex,
  HStack,
  Image,
  Select,
  useColorModeValue,
  keyframes,
} from '@chakra-ui/react'
import { useAppContext } from '../hooks/AppContextProvider'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

interface QRLayoutProps {
  children: React.ReactNode
}

const QRLayout = ({ children }: QRLayoutProps) => {
  const {
    language,
    updateLanguage,
    customLogoUrl,
  } = useAppContext()
  const { i18n } = useTranslation()

  // Memoize logo component for better performance
  const logoComponent = useMemo(() => {
    const defaultLogo = '/assets/logo.png';
    return (
      <Box
        position="relative"
        animation={`${fadeIn} 0.3s ease-in-out`}
        w="209px"
        h="41px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        borderRadius="sm"
      >
        <Image
          src={'/assets/logo.png'}
          alt={'logo'}
          fallbackSrc={defaultLogo}
          loading="lazy"
          maxW="100%"
          maxH="100%"
          w="auto"
          h="auto"
          objectFit="contain"
          _hover={{
            transform: 'scale(1.02)',
          }}
        />
      </Box>
    );
  }, [customLogoUrl]);

  return (
    <Box minHeight="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      {/* Header */}
      <Box
        bg={useColorModeValue('white', 'gray.800')}
        borderBottom="1px"
        borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <Container maxW="container.xl">
          <Flex
            h="70px"
            alignItems="center"
            justifyContent="space-between"
            py={4}
          >
            {/* Logo */}
            <Box>
              {logoComponent}
            </Box>

            {/* Language Selector */}
            <HStack spacing={4}>
              <Select
                value={language || i18n.language}
                onChange={(e) => {
                  updateLanguage(e.target.value)
                  i18n.changeLanguage(e.target.value)
                }}
                variant="outline"
                size="sm"
                w="auto"
                minW="80px"
                bg={useColorModeValue('white', 'gray.700')}
              >
                <option value="en">EN</option>
                <option value="ja">JP</option>
              </Select>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box as="main" flex="1">
        {children}
      </Box>
    </Box>
  )
}

export default QRLayout 